/**
 * Sync — authoritative-host snapshot/input protocol on top of Network.
 *
 * Wire protocol (all messages are JSON-serializable objects):
 *
 *   Client → host:
 *     { t: 'INPUT',  s: <stepIndex>, i: <inputObj> }
 *     { t: 'PING',   ts: <clientPerfNow> }
 *     { t: 'STATE_REQ' }
 *
 *   Host → client(s):
 *     { t: 'WELCOME', s: <stepIndex>, seed: <seedHex>, players: [...], settings: {...} }
 *     { t: 'SNAPSHOT', s: <stepIndex>, snap: <gameSnapshot> }
 *     { t: 'EVENT', name: <str>, payload: <any> }
 *     { t: 'PONG', ts: <echoedClientTs>, hostStep: <stepIndex> }
 *     { t: 'KICK', reason: <str> }
 *     { t: 'PLAYERS', list: [...] }   // Updated roster on join/leave.
 *
 * The host runs the simulation. Clients only send their inputs and render
 * snapshots. Local player input prediction is left to game code if it wants
 * it; this layer focuses on correctness over latency-hiding tricks.
 */

export const MSG = Object.freeze({
  INPUT: 'INPUT',
  PING: 'PING',
  PONG: 'PONG',
  STATE_REQ: 'STATE_REQ',
  WELCOME: 'WELCOME',
  SNAPSHOT: 'SNAPSHOT',
  EVENT: 'EVENT',
  KICK: 'KICK',
  PLAYERS: 'PLAYERS'
});

export class Sync {
  /**
   * @param {object} opts
   * @param {import('./Network.js').Network} opts.network
   * @param {number} opts.snapshotRateHz - How often the host emits snapshots.
   * @param {number} opts.tickRateHz - Simulation frequency (must match Tick).
   */
  constructor(opts) {
    this.net = opts.network;
    this.snapshotRateHz = opts.snapshotRateHz;
    this.tickRateHz = opts.tickRateHz;

    /** Latest input from each player, keyed by playerId. Cleared after consumption. */
    this.latestInputs = new Map();

    /** Last applied snapshot (clients only). */
    this.lastSnapshot = null;

    /** Buffer of recent snapshots (clients) for interpolation. */
    this.snapshotBuffer = [];
    this.snapshotBufferMax = 8;

    /** Stash of host-side player metadata, keyed by playerId. */
    this.players = new Map();

    /** Public callbacks the GameContainer wires up. */
    this.onWelcome = null;        // (welcomeMsg) => void  — clients
    this.onSnapshot = null;       // (snapMsg) => void     — clients
    this.onEvent = null;          // (name, payload) => void
    this.onPlayerInput = null;    // (playerId, inputObj) => void  — host hook
    this.onKick = null;           // (reason) => void

    // -Infinity so the *first* hostMaybeBroadcastSnapshot call always emits;
    // otherwise the first snapshot would be skipped if `nowMs` happened to
    // be inside the rate-limit window from t=0.
    this._lastSnapshotEmitMs = -Infinity;
    this._snapshotIntervalMs = 1000 / opts.snapshotRateHz;

    // Wire into the underlying transport.
    this.net.onMessage = (data, conn) => this._onMessage(data, conn);
  }

  // ─────────────────────────────────────────────────────────── host helpers

  /**
   * Host: send the welcome packet to a freshly joined client.
   * The seed must match for everyone in the room (deterministic RNG).
   */
  hostWelcome(playerId, { stepIndex, seed, settings }) {
    const msg = {
      t: MSG.WELCOME,
      s: stepIndex,
      seed,
      players: this._serializePlayers(),
      settings: settings || {}
    };
    this.net.sendTo(playerId, msg);
  }

  /** Host: broadcast the current player roster (after join/leave). */
  hostBroadcastPlayers() {
    this.net.broadcast({ t: MSG.PLAYERS, list: this._serializePlayers() });
  }

  /**
   * Host: broadcast a state snapshot if enough time has passed.
   * @param {number} nowMs - performance.now()
   * @param {number} stepIndex
   * @param {object} snap
   */
  hostMaybeBroadcastSnapshot(nowMs, stepIndex, snap) {
    if (nowMs - this._lastSnapshotEmitMs < this._snapshotIntervalMs) return false;
    this._lastSnapshotEmitMs = nowMs;
    this.net.broadcast({ t: MSG.SNAPSHOT, s: stepIndex, snap });
    return true;
  }

  /** Host or client: dispatch a one-off named event. */
  emitEvent(name, payload) {
    const msg = { t: MSG.EVENT, name, payload };
    if (this.net.isHost) this.net.broadcast(msg);
    else this.net.sendToHost(msg);
  }

  /**
   * Host: snapshot the inputs received this tick and clear the buffer.
   * Returns a `{ playerId: inputObj }` map. Players with no input get `{}`.
   */
  hostDrainInputs() {
    const drained = {};
    for (const id of this.net.getPlayerIds()) {
      drained[id] = this.latestInputs.get(id) || {};
    }
    return drained;
  }

  /** Host: register a new player joining the simulation. */
  hostAddPlayer(playerId, playerName) {
    this.players.set(playerId, { id: playerId, name: playerName });
  }

  hostRemovePlayer(playerId) {
    this.players.delete(playerId);
    this.latestInputs.delete(playerId);
  }

  // ─────────────────────────────────────────────────────────── client helpers

  /** Client: send the current input state to the host. */
  clientSendInput(stepIndex, inputObj) {
    this.net.sendToHost({ t: MSG.INPUT, s: stepIndex, i: inputObj });
  }

  /** Client: ask the host for the freshest snapshot. */
  clientRequestState() {
    this.net.sendToHost({ t: MSG.STATE_REQ });
  }

  // ─────────────────────────────────────────────────────────── internals

  _serializePlayers() {
    const list = [];
    for (const [, p] of this.players) list.push({ id: p.id, name: p.name });
    return list;
  }

  _onMessage(data, conn) {
    if (!data || typeof data.t !== 'string') return;

    switch (data.t) {
      case MSG.INPUT: {
        // Host: stash input keyed by sender.
        const playerId = this._playerIdForConn(conn);
        if (playerId) {
          this.latestInputs.set(playerId, data.i || {});
          if (this.onPlayerInput) this.onPlayerInput(playerId, data.i || {});
        }
        break;
      }

      case MSG.PING: {
        // Host echoes back; latency layer will read it.
        if (this.net.isHost) {
          const reply = { t: MSG.PONG, ts: data.ts, hostStep: data.hostStep || 0 };
          // Reply only to the sender.
          for (const [, info] of this.net.connections) {
            if (info.connection === conn) {
              info.connection.send(reply);
              break;
            }
          }
        }
        break;
      }

      case MSG.PONG: {
        // Forwarded to the Latency module via onEvent? No — handled directly.
        if (this._onPong) this._onPong(data);
        break;
      }

      case MSG.STATE_REQ: {
        if (this._onStateRequest) this._onStateRequest(conn);
        break;
      }

      case MSG.WELCOME: {
        if (this.onWelcome) this.onWelcome(data);
        break;
      }

      case MSG.SNAPSHOT: {
        this.lastSnapshot = data;
        this.snapshotBuffer.push(data);
        if (this.snapshotBuffer.length > this.snapshotBufferMax) {
          this.snapshotBuffer.shift();
        }
        if (this.onSnapshot) this.onSnapshot(data);
        break;
      }

      case MSG.EVENT: {
        if (this.onEvent) this.onEvent(data.name, data.payload);
        break;
      }

      case MSG.PLAYERS: {
        // Update local roster on the client.
        this.players.clear();
        for (const p of data.list) this.players.set(p.id, p);
        if (this.onEvent) this.onEvent('players-changed', data.list);
        break;
      }

      case MSG.KICK: {
        if (this.onKick) this.onKick(data.reason || 'kicked');
        break;
      }
    }
  }

  _playerIdForConn(conn) {
    if (!conn) return null;
    for (const [id, info] of this.net.connections) {
      if (info.connection === conn) return id;
    }
    return null;
  }
}
